import React from 'react';
import AccordionRadioButton from './AccordionRadioButtons';
import { AddRemove } from './AddRemove';
import { Field, FieldData, RecordValue } from '../utils/types';

interface MappedFieldProps {
  field: Field;
  variant?: 'fixed' | 'add' | 'remove' | 'options';
  defaultExpanded?: boolean;
  onClick?: Function;
  relatedRecordId?: string;
}

const getValuesToArray = (values: any) => {
  return (Object.entries(values || {}) as Array<[string, FieldData]>).reduce(
    (acc: any[], [key, fieldData]) => {
      const { recordId, value } = fieldData;
      return [
        ...acc,
        ...(Array.isArray(value)
          ? value.map((v: any) => ({ recordId, value: v }))
          : [{ recordId, value }]),
      ];
    },
    [],
  );
};

const MappedField: React.FC<MappedFieldProps> = React.memo(
  ({
    field,
    variant = 'fixed',
    defaultExpanded = false,
    onClick = null,
    relatedRecordId = null,
  }) => {
    const { currentRecordData } = field;
    const valueFromField: any =
      relatedRecordId && field.recordData
        ? field.recordData[relatedRecordId]
        : currentRecordData.value;

    const handleClick = (
      e: React.SyntheticEvent<HTMLButtonElement>,
      recordValue: RecordValue,
    ) => {
      onClick?.(field, recordValue);
    };

    if (variant === 'options') {
      return (
        <AccordionRadioButton
          defaultExpanded={defaultExpanded}
          field={field}
          options={field.reviewProps?.optionsToShow || []}
          onClick={handleClick}
          isAI={!field.reviewProps?.hasConflictData}
        />
      );
    }

    return (
      <AddRemove
        icon={variant === 'remove' ? 'remove' : 'add'}
        field={field}
        onClick={variant === 'fixed' ? undefined : handleClick}
        values={
          variant === 'remove' || variant === 'fixed'
            ? getValuesToArray(field.readyToImportValues?.added)
            : getValuesToArray({
                [relatedRecordId as string]: {
                  recordId: relatedRecordId,
                  value: valueFromField,
                },
              })
        }
      />
    );
  },
  (prevProps, nextProps) =>
    prevProps.defaultExpanded === nextProps.defaultExpanded &&
    prevProps.field === nextProps.field &&
    prevProps.variant === nextProps.variant &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.field.reviewProps?.optionsToShow.length ===
      nextProps?.field.reviewProps?.optionsToShow.length,
);

export { MappedField };
